/**
 * Decides which failed API calls the shell banner may capture and retry.
 *
 * Retryable = safe GET (or equivalent) data loads that screens need to paint.
 * Ignored = auth plumbing, health noise, or user-driven mutations (re-submit in UI).
 */

export type ApiFailureKey =
  | 'carriers'
  | 'senders'
  | 'accounts'
  | 'jobs'
  | 'audit'
  | 'uploads'
  | 'admin-users'
  | 'admin-invites';

export interface ApiFailureClass {
  /** Stable key used to dedupe captures and match refresh handlers. */
  key: ApiFailureKey;
  /** Why this call exists in the product. */
  purpose: string;
  /** Safe to re-invoke from Try Again without duplicating a user action. */
  retryable: boolean;
}

/** Strip query/hash; work with absolute or relative /api/... URLs. */
export function normalizeApiPath(url: string): string {
  try {
    const path = url.startsWith('http') ? new URL(url).pathname : url.split('?')[0].split('#')[0];
    return path.replace(/\/+$/, '') || '/';
  } catch {
    return url.split('?')[0].split('#')[0];
  }
}

/**
 * Classify a request for banner capture.
 * Returns null when the call must not participate in Try Again capture at all.
 */
export function classifyApiFailure(method: string, url: string): ApiFailureClass | null {
  const verb = method.toUpperCase();
  const path = normalizeApiPath(url).toLowerCase();

  // Auth sign-in / session plumbing — never capture for shell retry.
  if (
    path.includes('/auth/login') ||
    path.includes('/auth/otp') ||
    path.includes('/auth/status') ||
    path.includes('/auth/methods') ||
    path.includes('/auth/microsoft') ||
    path.includes('/auth/logout')
  ) {
    return null;
  }

  // Health probe — informational only; not a user-facing data load.
  if (path.endsWith('/health') || path.endsWith('/api/health')) {
    return null;
  }

  // Mutations and downloads — user must re-trigger from the feature UI.
  if (verb !== 'GET') {
    return null;
  }
  if (
    path.includes('/export') ||
    path.includes('/publish') ||
    path.includes('/rollback') ||
    path.includes('/setup-account') ||
    path.includes('/upload')
  ) {
    return null;
  }

  if (path.includes('/accounts')) {
    return {
      key: 'accounts',
      purpose: 'Load SmartFreight accounts for the selected carrier (Create account table).',
      retryable: true
    };
  }
  if (path.includes('/carriers')) {
    return {
      key: 'carriers',
      purpose: 'Load carrier list for account setup and uploads.',
      retryable: true
    };
  }
  if (path.includes('/senders')) {
    return {
      key: 'senders',
      purpose: 'Load sender list for account setup.',
      retryable: true
    };
  }
  if (path.includes('/jobs')) {
    return {
      key: 'jobs',
      purpose: 'Load publish history for the rates page.',
      retryable: true
    };
  }
  if (path.includes('/audit')) {
    return {
      key: 'audit',
      purpose: 'Load audit trail records for the admin audit page.',
      retryable: true
    };
  }
  if (path.includes('/uploads')) {
    return {
      key: 'uploads',
      purpose: 'Load recent uploads for review context.',
      retryable: true
    };
  }
  if (path.includes('/auth/users')) {
    return {
      key: 'admin-users',
      purpose: 'Load organisation members for the Admin Panel.',
      retryable: true
    };
  }
  if (path.includes('/auth/invites') && verb === 'GET') {
    return {
      key: 'admin-invites',
      purpose: 'Load pending invitations for the Admin Panel.',
      retryable: true
    };
  }

  // Unknown GET — show banner message via interceptor, but do not auto-retry blindly.
  return null;
}
